"use client";

import { cn } from "@/lib";
import AppContainer from "../appContainer";
import HistoryItem from "../inquiry-history/historyItem";
import { CustomButton, FlexCenterCol, FlexCol } from "../ui";
import ReactPaginate from "react-paginate";
import { BdAltArrowLeft, BdAltArrowRight } from "solar-icon-react/bd";
import Image from "next/image";
import { useGetStoredData } from "@/hooks";
import { getStoredDataAdapter } from "@/lib/adapters/getStoredDataAdapter";
import { TVariantType } from "@/interfaces";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import Link from "next/link";

interface InquiryHistorySectionProps {
  className?: string;
}

const InquiryHistorySection = ({ className }: InquiryHistorySectionProps) => {
  const t = useTranslations();
  const { data: storedData } = useGetStoredData();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentPage = Number(searchParams?.get("page")) || 1;
  const currentLimit = Number(searchParams?.get("limit")) || 6;
  const total = storedData?.total ?? 0;
  const adaptedStoredData = getStoredDataAdapter(storedData?.data ?? []);
  const pageCount = total ? Math.ceil(total / currentLimit) : 0;
  const handlePageClick = (event: { selected: number }) => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("page", String(event.selected + 1));
    params.set("limit", String(currentLimit));
    router.push(`${pathname}?${params.toString()}`);
  };
  const nextControlPaginationClass = cn(
    "cursor-pointer hover:text-primary-main transition-colors",
    currentPage == pageCount && "pointer-events-none! opacity-50",
  );
  const previousControlPaginationClass = cn(
    "cursor-pointer hover:text-primary-main transition-colors",
    currentPage === 1 && "pointer-events-none! opacity-50",
  );
  return (
    <section className={cn("w-full", className)}>
      <AppContainer className={cn("h-auto")}>
        <div
          className={cn(
            "mx-auto rounded-[18px] p-[2.6px] shadow sm:w-[90%] md:w-[80%]",
            "from-primary-main/100 to-success-main/0 bg-gradient-to-b",
          )}
        >
          <FlexCol
            className={cn(
              "size-full gap-4 overflow-hidden rounded-2xl bg-white px-2 py-6 sm:gap-8 md:p-10",
            )}
          >
            {!adaptedStoredData || adaptedStoredData.length === 0 ? (
              <FlexCenterCol gap={6} className="size-full grow">
                <figure className={cn("basis-1/2")}>
                  <Image
                    src="/svg/no-data-amico.svg"
                    width={0}
                    height={0}
                    sizes="100vw"
                    className={cn("size-full h-60 object-contain")}
                    alt={t("inquiryHistory.noDataAlt")}
                  />
                </figure>
                <p className="text-xl font-bold">
                  {t("inquiryHistory.noDataMessage")}
                </p>
                <Link href={routes.main.SERVICES.primaryPath} className="mt-6">
                  <CustomButton
                    id="inquiry-history-services-button"
                    className={cn("bg-primary-main! mx-auto w-max px-4!")}
                    iconPosition="end"
                    icon={<BdAltArrowLeft size={25} />}
                  >
                    {t("services.servicesPage")}
                  </CustomButton>
                </Link>
              </FlexCenterCol>
            ) : (
              adaptedStoredData.map((item) => (
                <HistoryItem
                  key={item.id}
                  date={item.date}
                  title={item.title}
                  categoryIcon={item.categoryIcon}
                  id={item.id}
                  inquiryFlag={item.flag}
                  variant={item.variant as TVariantType}
                  href={`${routes.main.INQUIRY_DETAILS.primaryPath}/${item.id}`}
                  input={item.input ?? ""}
                  linkTitle={t("inquiryHistory.viewInquiry")}
                  status={item.status}
                  code={item.code}
                  message={item.message}
                  hasData={item.hasData}
                />
              ))
            )}
            <div className="noselect dir-ltr mr-auto w-max text-xs xl:text-sm!">
              <ReactPaginate
                breakLabel="..."
                nextLabel={
                  <div className="flex items-center gap-2">
                    <span>{t("pagination.next")}</span>
                    <div
                      className={cn(
                        "flex items-center justify-center",
                        "w-4 sm:w-5 md:w-[23px] lg:w-[25px]",
                      )}
                    >
                      <BdAltArrowRight className="size-full!" />
                    </div>
                  </div>
                }
                previousLabel={
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "flex items-center justify-center",
                        "w-4 sm:w-5 md:w-[23px] lg:w-[25px]",
                      )}
                    >
                      <BdAltArrowLeft className="size-full!" />
                    </div>
                    <span>{t("pagination.previous")}</span>
                  </div>
                }
                onPageChange={handlePageClick}
                pageRangeDisplayed={1}
                pageCount={pageCount}
                forcePage={currentPage - 1}
                renderOnZeroPageCount={null}
                className={cn("text-custom-gray-200 flex items-center gap-5")}
                pageClassName="cursor-pointer"
                nextClassName={nextControlPaginationClass}
                previousClassName={previousControlPaginationClass}
                activeClassName="text-primary-main"
              />
            </div>
          </FlexCol>
        </div>
      </AppContainer>
    </section>
  );
};

export default InquiryHistorySection;
